import React from 'react';
import leagues from './League';

import { Card, AreaChart, Text, Title } from "@tremor/react";

const EdgeBar = ({ label, pct, color, textColor }) => (
  <div className="flex items-center gap-3">
    <span className="text-xs font-medium text-gray-500 w-24 text-right shrink-0">{label}</span>
    <div className="flex-1 bg-gray-100 rounded-full h-7 relative overflow-hidden">
      <div
        className={`h-full rounded-full ${color} transition-all duration-500`}
        style={{ width: `${Math.min(pct, 100)}%` }}
      />
      <span className={`absolute inset-0 flex items-center px-3 text-sm font-bold ${textColor}`}>
        {pct.toFixed(1)}%
      </span>
    </div>
  </div>
);

const BetCard = ({ betDetails }) => {
  const { league, pick_description, pick_justification, confidence_level } = betDetails;

  const leagueObj = leagues.find(l => l.value === league);

  const isNewFormat = betDetails.estimated_true_probability !== undefined;
  const isLegacyFormat = betDetails.recent_trend_stat_values && betDetails.recent_trend_stat_name;

  if (!isNewFormat && !isLegacyFormat) {
    return "✅ Saved Bet";
  }

  const confidenceBg = { high: "bg-emerald-500", medium: "bg-amber-500", low: "bg-rose-500" };
  const confidenceText = { high: "High", medium: "Med", low: "Low" };

  const truePct = isNewFormat ? betDetails.estimated_true_probability * 100 : 0;
  const impliedPct = isNewFormat ? betDetails.implied_probability * 100 : 0;
  const edgePct = isNewFormat ? betDetails.edge * 100 : 0;
  const hasEdge = edgePct > 0;

  return (
    <Card className="w-full mx-auto shadow rounded p-4" decoration="top" decorationColor={isNewFormat ? (hasEdge ? "emerald" : "rose") : "indigo"}>
      <Title className="text-lg font-semibold text-gray-600">
        <span className="inline-block bg-gray-300 rounded-md px-2 py-1 text-sm lg:text-md font-semibold text-black mr-2">
          {leagueObj ? leagueObj.display : league}
        </span>
        Pick: {pick_description}
      </Title>

      {isNewFormat ? (
        <>
          {/* Probability comparison bars */}
          <div className="mt-5 space-y-2">
            <EdgeBar label="Our Edge" pct={truePct} color="bg-indigo-500" textColor="text-white" />
            <EdgeBar label="Implied Odds" pct={impliedPct} color="bg-gray-400" textColor="text-gray-800" />
          </div>

          {/* Edge callout */}
          <div className="mt-3 flex items-center justify-between">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-bold ${
                hasEdge ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"
              }`}>
                {hasEdge ? "+" : ""}{edgePct.toFixed(1)}% edge
              </span>
              {confidence_level && (
                <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold text-white ${confidenceBg[confidence_level] || "bg-gray-500"}`}>
                  {confidenceText[confidence_level] || confidence_level}
                </span>
              )}
            </div>
            {betDetails.base_rate_sample_size && (
              <span className="text-xs text-gray-400">{betDetails.base_rate_sample_size}G sample</span>
            )}
          </div>

          {/* Odds + line */}
          {(betDetails.decimal_odds || betDetails.line) && (
            <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
              {betDetails.decimal_odds && <span>Odds: <strong>{betDetails.decimal_odds}</strong></span>}
              {betDetails.line && <span>Line: <strong>{betDetails.line}</strong></span>}
              {betDetails.home_team && betDetails.away_team && (
                <span>{betDetails.away_team} @ {betDetails.home_team}</span>
              )}
            </div>
          )}

          {/* Adjustment reasoning */}
          {betDetails.adjustment_reasoning && (
            <Text className="text-sm text-gray-700 mt-3 italic border-l-2 border-indigo-400 pl-3">
              {betDetails.adjustment_reasoning}
            </Text>
          )}

          {/* DK betslip link */}
          {betDetails.dk_betslip_link && (
            <a
              href={betDetails.dk_betslip_link}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-500 transition-colors"
            >
              🎰 Place on DraftKings
            </a>
          )}
        </>
      ) : (
        <>
          <Text className="text-center mt-4 font-bold text-md">Recent {betDetails.recent_trend_stat_name}</Text>
          <AreaChart
            className="h-56 mt-4"
            data={betDetails.recent_trend_stat_values.slice().reverse().map((value, index) => {
              let label = index === 0 ? "Oldest" : index === betDetails.recent_trend_stat_values.length - 1 ? "Most Recent" : index.toString();
              let data = { index: label, [betDetails.recent_trend_stat_name]: value };
              if (betDetails.stat_target_value !== undefined) data["Line"] = betDetails.stat_target_value;
              return data;
            })}
            index="index"
            yAxisWidth={50}
            categories={[betDetails.recent_trend_stat_name, "Line"]}
            colors={["indigo", "cyan"]}
            curveType="monotone"
            startEndOnly={true}
            showXAxis={true}
            showLegend={false}
          />
        </>
      )}

      <Text className="text-sm text-gray-600 mt-3">
        {pick_justification}
      </Text>
    </Card>
  );
};

export default BetCard;